import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ReportCategoryCard from './components/ReportCategoryCard';
import ReportFilters from './components/ReportFilters';
import AIInsightsPanel from './components/AIInsightsPanel';
import ReportViewer from './components/ReportViewer';
import QuickStatsGrid from './components/QuickStatsGrid';
import RecentReportsList from './components/RecentReportsList';
import { reportsService } from '../../services/reportsService';
import { useAuth } from '../../contexts/AuthContext';

const ReportsDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [activeView, setActiveView] = useState('overview');
  const [reportFilters, setReportFilters] = useState({});
  const [reportCategories, setReportCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, userProfile } = useAuth();

  useEffect(() => {
    // Wait for both user and userProfile to be available
    if (user && userProfile && !userProfile?.isFallback) {
      loadReportCategories();
    }
  }, [user, userProfile]);

  const loadReportCategories = async () => {
    if (!user) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Remove hardcoded fallback - use only actual user's organization ID
      const organizationId = userProfile?.organization_id;
      
      if (!organizationId) {
        throw new Error('No organization ID found for user. Please check your account setup.');
      }
      
      console.debug('Loading reports for organization:', organizationId);
      
      const categoriesData = await reportsService?.getReportCategories(organizationId);
      setReportCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load report categories:', error);
      setError(`Failed to load report categories: ${error?.message || 'Unknown error'}`);
      setReportCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const getAllReports = () => {
    const allReports = [];
    reportCategories?.forEach(category => {
      category?.reports?.forEach(report => {
        allReports?.push({
          ...report,
          category: category?.name,
          categoryId: category?.id
        });
      });
    });
    return allReports;
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setActiveView('reports-list');
  };

  const handleReportSelect = (report) => {
    setSelectedReport(report);
    setActiveView('report');
  };

  const handleFiltersChange = (filters) => {
    setReportFilters(filters);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setActiveView('categories');
  };

  const handleViewAllReports = () => {
    console.log('handleViewAllReports called - switching to all-reports view');
    setActiveView('all-reports');
    setSelectedCategory(null);
    setSelectedReport(null);
    
    // Scroll to top for better UX
    window?.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Add visual feedback by highlighting the All Reports tab
    setTimeout(() => {
      const allReportsTab = document?.querySelector('[data-tab="all-reports"]');
      if (allReportsTab) {
        allReportsTab?.classList?.add('ring-2', 'ring-primary/50');
        setTimeout(() => {
          allReportsTab?.classList?.remove('ring-2', 'ring-primary/50');
        }, 1000);
      }
    }, 100);
  };

  // Enhanced header button functionalities
  const handleScheduledReports = () => {
    console.log('Opening Scheduled Reports management');
    // TODO: Implement actual scheduled reports functionality
    alert('Scheduled Reports: This feature will be implemented to configure automatic report generation and delivery schedules.');
  };

  const handleReportSettings = () => {
    console.log('Opening Report Settings');
    // TODO: Implement actual report settings functionality
    alert('Report Settings: This feature will be implemented to configure default formats, themes, and preferences.');
  };

  const handleCustomReports = () => {
    console.log('Opening Custom Reports builder');
    // TODO: Implement actual custom reports functionality
    alert('Custom Reports: This feature will be implemented to create custom reports with drag-and-drop builder and AI assistance.');
  };

  // Enhanced AI Analytics button functionality
  const handleTryAIAnalytics = () => {
    console.log('Opening AI Analytics feature');
    setActiveView('insights');
    
    // Scroll to top for better UX
    window?.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Add visual feedback
    setTimeout(() => {
      const insightsTab = document?.querySelector('[data-tab="insights"]');
      if (insightsTab) {
        insightsTab?.classList?.add('ring-2', 'ring-accent/50');
        setTimeout(() => {
          insightsTab?.classList?.remove('ring-2', 'ring-accent/50');
        }, 2000);
      }
    }, 100);
  };

  // Enhanced filter functionality in All Reports view
  const handleAllReportsFilter = () => {
    console.log('Opening advanced filters for all reports');
    // TODO: Implement actual advanced filtering
    alert('Advanced Filters: This feature will be implemented to filter by date range, category, status, creator, and more.');
  };

  const handleAllReportsSearch = () => {
    console.log('Opening search for all reports');
    // TODO: Implement actual search functionality
    const searchQuery = prompt('Search reports by name or description:');
    if (searchQuery) {
      console.log(`Searching for: ${searchQuery}`);
      alert(`Search functionality will be implemented for "${searchQuery}".`);
    }
  };

  useEffect(() => {
    document.title = 'Reports Dashboard - AccounTech AI';
  }, []);

  // Add effect to handle view changes
  useEffect(() => {
    console.log('Active view changed to:', activeView);
  }, [activeView]);

  const renderReportsList = (reports, categoryName) => {
    if (!reports || reports?.length === 0) {
      return (
        <div className="bg-card border border-border rounded-lg p-8">
          <div className="text-center">
            <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Reports Available</h3>
            <p className="text-muted-foreground">Reports for this category will appear here once data is available.</p>
            <Button variant="outline" onClick={handleBackToCategories} iconName="ArrowLeft" className="mt-4">
              Back to Categories
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-card border border-border rounded-lg">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground">{categoryName}</h3>
              <p className="text-sm text-muted-foreground">All reports in this category</p>
            </div>
            <Button variant="outline" onClick={handleBackToCategories} iconName="ArrowLeft">
              Back to Categories
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/20">
                <th className="text-left py-4 px-6 font-semibold text-foreground">Report Name</th>
                <th className="text-left py-4 px-6 font-semibold text-foreground">Last Visited</th>
                <th className="text-left py-4 px-6 font-semibold text-foreground">Created By</th>
                <th className="text-left py-4 px-6 font-semibold text-foreground">More Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports?.map((report) => (
                <tr 
                  key={report?.id} 
                  className="border-b border-border hover:bg-muted/30 cursor-pointer"
                  onClick={() => handleReportSelect(report)}
                >
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${
                        report?.status === 'completed' ? 'bg-success' : 'bg-warning'
                      }`}></div>
                      <span className="font-medium text-foreground hover:text-primary transition-colors">
                        {report?.name}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-muted-foreground">
                    {report?.lastVisited || '-'}
                  </td>
                  <td className="py-4 px-6 text-muted-foreground">
                    {report?.createdBy || 'System'}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        iconName="Eye"
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleReportSelect(report);
                        }}
                      >
                        View
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        iconName="Download"
                        onClick={(e) => e?.stopPropagation()}
                      >
                        Export
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        iconName="MoreVertical"
                        onClick={(e) => e?.stopPropagation()}
                      >
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderAllReportsList = () => {
    const allReports = getAllReports();
    
    if (!allReports || allReports?.length === 0) {
      return (
        <div className="bg-card border border-border rounded-lg p-8">
          <div className="text-center">
            <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Reports Available</h3>
            <p className="text-muted-foreground">Reports will appear here once data is available from the backend.</p>
            <Button 
              variant="outline" 
              onClick={() => setActiveView('overview')}
              iconName="ArrowLeft"
              className="mt-4"
            >
              Back to Overview
            </Button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="bg-card border border-border rounded-lg">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground">All Reports</h3>
              <p className="text-sm text-muted-foreground">Complete list of all available reports across all categories</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" iconName="Filter">
                Filter
              </Button>
              <Button variant="outline" iconName="Search">
                Search
              </Button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/20">
                <th className="text-left py-4 px-6 font-semibold text-foreground">Report Name</th>
                <th className="text-left py-4 px-6 font-semibold text-foreground">Category</th>
                <th className="text-left py-4 px-6 font-semibold text-foreground">Last Visited</th>
                <th className="text-left py-4 px-6 font-semibold text-foreground">Created By</th>
                <th className="text-left py-4 px-6 font-semibold text-foreground">More Actions</th>
              </tr>
            </thead>
            <tbody>
              {allReports?.map((report) => (
                <tr 
                  key={`${report?.categoryId}-${report?.id}`} 
                  className="border-b border-border hover:bg-muted/30 cursor-pointer"
                  onClick={() => handleReportSelect(report)}
                >
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${
                        report?.status === 'completed' ? 'bg-success' : 'bg-warning'
                      }`}></div>
                      <span className="font-medium text-foreground hover:text-primary transition-colors">
                        {report?.name}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="px-2 py-1 text-xs bg-muted/50 text-muted-foreground rounded-full">
                      {report?.category}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-muted-foreground">
                    {report?.lastVisited || '-'}
                  </td>
                  <td className="py-4 px-6 text-muted-foreground">
                    {report?.createdBy || 'System'}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        iconName="Eye"
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleReportSelect(report);
                        }}
                      >
                        View
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        iconName="Download"
                        onClick={(e) => e?.stopPropagation()}
                      >
                        Export
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        iconName="MoreVertical"
                        onClick={(e) => e?.stopPropagation()}
                      >
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Show loading state during initial load
  if (loading && reportCategories?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6 space-y-6">
            <div className="text-center py-12">
              <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
              <h3 className="text-lg font-medium text-foreground mb-2">Loading Reports Dashboard</h3>
              <p className="text-muted-foreground">
                Fetching your financial data and report categories...
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show error state if loading failed
  if (error && reportCategories?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6 space-y-6">
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Error Loading Dashboard</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button 
                variant="outline" 
                onClick={loadReportCategories}
                iconName="RefreshCw"
              >
                Try Again
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Reports Dashboard</h1>
              <p className="text-muted-foreground mt-2">
                Comprehensive financial reporting with AI-powered analytics and insights
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button 
                variant="outline" 
                iconName="Calendar"
                onClick={handleScheduledReports}
              >
                Scheduled Reports
              </Button>
              <Button 
                variant="outline" 
                iconName="Settings"
                onClick={handleReportSettings}
              >
                Report Settings
              </Button>
              <Button 
                iconName="Plus"
                onClick={handleCustomReports}
              >
                Custom Reports
              </Button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex flex-wrap space-x-1 bg-muted/30 rounded-lg p-1 w-fit">
            <button
              onClick={() => setActiveView('overview')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeView === 'overview' ? 'bg-background text-foreground shadow-sm ring-2 ring-primary/20' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-tab="overview"
            >
              <Icon name="LayoutDashboard" size={16} className="inline mr-2" />
              Overview
            </button>
            <button
              onClick={() => setActiveView('categories')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeView === 'categories' ? 'bg-background text-foreground shadow-sm ring-2 ring-primary/20' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-tab="categories"
            >
              <Icon name="Grid3X3" size={16} className="inline mr-2" />
              Report Categories
            </button>
            <button
              onClick={() => setActiveView('all-reports')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeView === 'all-reports' ? 'bg-background text-foreground shadow-sm ring-2 ring-primary/20' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-tab="all-reports"
            >
              <Icon name="FileText" size={16} className="inline mr-2" />
              All Reports
              {activeView === 'all-reports' && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-primary bg-primary/10 rounded-full">
                  Active
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveView('report')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeView === 'report' ? 'bg-background text-foreground shadow-sm ring-2 ring-primary/20' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-tab="report"
            >
              <Icon name="BarChart3" size={16} className="inline mr-2" />
              Report Viewer
            </button>
            <button
              onClick={() => setActiveView('insights')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeView === 'insights' ? 'bg-background text-foreground shadow-sm ring-2 ring-primary/20' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-tab="insights"
            >
              <Icon name="Brain" size={16} className="inline mr-2" />
              AI Analytics
            </button>
          </div>

          {/* Active View Indicator */}
          {activeView === 'all-reports' && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={20} className="text-primary" />
                <span className="text-sm font-medium text-primary">Now viewing: All Reports</span>
                <span className="text-sm text-muted-foreground">
                  {getAllReports()?.length > 0 ? `Complete list of ${getAllReports()?.length} reports across all categories` : 'No reports available'}
                </span>
              </div>
            </div>
          )}

          {/* Advanced Analytics Banner */}
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Advanced Financial Analytics for AccounTech AI</h3>
                <p className="text-muted-foreground">
                  Unlock powerful insights with AI-driven financial analytics and custom reporting capabilities
                </p>
              </div>
              <Button 
                iconName="Zap" 
                variant="outline"
                onClick={handleTryAIAnalytics}
                className="hover:bg-primary/10 hover:border-primary/50 transition-all duration-200"
              >
                Try AI Analytics
              </Button>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3">
                <Icon name="Loader2" size={24} className="animate-spin text-primary" />
                <span className="text-foreground">Loading reports...</span>
              </div>
            </div>
          )}

          {/* Overview View */}
          {!loading && activeView === 'overview' && (
            <div className="space-y-6">
              <QuickStatsGrid />
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <RecentReportsList 
                  onReportSelect={handleReportSelect} 
                  onViewAllReports={handleViewAllReports}
                />
                <AIInsightsPanel />
              </div>
            </div>
          )}

          {/* Categories View */}
          {!loading && activeView === 'categories' && (
            <div className="space-y-6">
              <ReportFilters onFiltersChange={handleFiltersChange} activeFilters={reportFilters} />
              
              {reportCategories?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {reportCategories?.map((category) => (
                    <ReportCategoryCard
                      key={category?.id}
                      category={category}
                      onSelect={handleCategorySelect}
                      isActive={selectedCategory?.id === category?.id}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-card border border-border rounded-lg p-8">
                  <div className="text-center">
                    <Icon name="Grid3X3" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">No Report Categories Available</h3>
                    <p className="text-muted-foreground">Report categories will appear here once configured in the backend.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* All Reports View */}
          {!loading && activeView === 'all-reports' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">All Reports</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {getAllReports()?.length > 0 
                      ? `Showing ${getAllReports()?.length} reports from ${reportCategories?.length} categories`
                      : 'No reports available'
                    }
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setActiveView('overview')}
                  iconName="ArrowLeft"
                >
                  Back to Overview
                </Button>
              </div>
              <ReportFilters onFiltersChange={handleFiltersChange} activeFilters={reportFilters} />
              {renderAllReportsList()}
            </div>
          )}

          {/* Individual Category Reports List */}
          {!loading && activeView === 'reports-list' && selectedCategory && (
            <div className="space-y-6">
              <ReportFilters onFiltersChange={handleFiltersChange} activeFilters={reportFilters} />
              {renderReportsList(selectedCategory?.reports, selectedCategory?.name)}
            </div>
          )}

          {/* Report Viewer */}
          {!loading && activeView === 'report' && (
            <div className="space-y-6">
              <ReportFilters onFiltersChange={handleFiltersChange} activeFilters={reportFilters} />
              <ReportViewer selectedReport={selectedReport} reportData={{}} />
            </div>
          )}

          {/* AI Insights View */}
          {!loading && activeView === 'insights' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <AIInsightsPanel />
                </div>
                <div>
                  <RecentReportsList 
                    onReportSelect={handleReportSelect} 
                    onViewAllReports={handleViewAllReports}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ReportsDashboard;